import { getApi } from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { IAddJobApplicantLink, IJobApplicantLink, JobApplicantErrorKey } from 'types/employee/IJobApplicantLink';

const BASE_URL = 'JobApplicants';

interface JobApplicantErrorDetails {
  locationIds?: number[];
  linkId?: number;
  companyId?: number;
}

interface JobApplicantErrorBody {
  error?: string;
  message?: string | null;
  details?: JobApplicantErrorDetails;
  /** Present instead of the above on a plain ASP.NET ModelState failure. */
  errors?: Record<string, string[]>;
}

export interface ParsedJobApplicantError {
  key: JobApplicantErrorKey | null;
  details: JobApplicantErrorDetails;
  status?: number;
}

/**
 * Read the stable error key + structured details from the shared ErrorResponse body.
 *
 * Branches on `error`, never on `message` — `message` is localized only when a backend
 * translation exists and is null otherwise, so the key is what we translate client-side.
 *
 * One exception the API documents: a plain model-validation failure (locationIds omitted
 * entirely, non-integer body) returns the standard ModelState 400 with an `errors` map and
 * no `error` field. We fold that onto BRANCH_REQUIRED when it concerns locationIds, since
 * that is the only field we send.
 */
export const parseJobApplicantError = (error: unknown): ParsedJobApplicantError => {
  const axiosError = error as AxiosError<JobApplicantErrorBody>;
  const body = axiosError?.response?.data;
  const status = axiosError?.response?.status;

  if (!body?.error && body?.errors) {
    const mentionsLocationIds = Object.keys(body.errors).some(field => field.toLowerCase().includes('locationids'));
    return {
      key: mentionsLocationIds ? '[JOB_APPLICANTS] BRANCH_REQUIRED' : null,
      details: {},
      status,
    };
  }

  return {
    key: (body?.error as JobApplicantErrorKey) ?? null,
    details: body?.details ?? {},
    status,
  };
};

export const getJobApplicantLinksRequest = async (companyId: number): Promise<IJobApplicantLink[]> => {
  const response: AxiosResponse<IJobApplicantLink[]> = await getApi().get(`${BASE_URL}/${companyId}/Links`);
  return response.data;
};

export const addJobApplicantLinkRequest = async (
  companyId: number,
  payload: IAddJobApplicantLink,
): Promise<IJobApplicantLink> => {
  const response: AxiosResponse<IJobApplicantLink> = await getApi().post(`${BASE_URL}/${companyId}/Links`, payload);
  return response.data;
};

/** Idempotent server-side: deleting an already-deleted link is also a 204. */
export const deleteJobApplicantLinkRequest = async (companyId: number, id: number): Promise<void> => {
  await getApi().delete(`${BASE_URL}/${companyId}/Links/${id}`);
};

/**
 * Admin-only per-company feature state (US 7492).
 *
 * Needed because an Admin's /Profile payload carries no company context, so the
 * `featureJobApplicantsEnabled` flag on it says nothing about the company an admin has
 * navigated to. Managers are scoped to their company and use the profile flag instead.
 */
export const getJobApplicantFeatureRequest = async (companyId: number): Promise<boolean> => {
  const response: AxiosResponse<{ companyId: number; enabled: boolean }> = await getApi().get(
    `${BASE_URL}/Feature/${companyId}`,
  );
  return response.data.enabled;
};
